import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usePersistedDataStore } from '../../../store';
import StableMuiDialog from '../../../../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import logger from '../../../../utils/logger';
// import { generateEmailPreview } from '../services/offerSendingService.js';
import axiosInstance from '../../../../fetchConfig/axiosConfig';
import { sanitizeFinalizedOffers, sanitizeAllRows } from '../../../../utils/jsonSanitizer';



// I THINK THIS COMPONENT IS NOW UNUSED ENTIRELY 

/**
 * Generate email preview data for assessor notification
 */
const generateEmailPreview = async (options) => {
  const { muniCode, muniString, taxYear, courtDate, finalizedOffers, villageRun, downloadUrl, csvFilename, caseInfo, user } = options;
  
  // Sanitize the data to remove non-JSON elements
  const sanitizedFinalizedOffers = sanitizeFinalizedOffers(finalizedOffers);
  
  logger.log('Original finalizedOffers count:', finalizedOffers?.length);
  logger.log('Sanitized finalizedOffers count:', sanitizedFinalizedOffers?.length);
  
  try {
    const response = await axiosInstance.post('/generate_assessor_email_preview', {
      muniCode,
      muniString,
      taxYear,
      courtDate,
      finalizedOffers: sanitizedFinalizedOffers,
      villageRun,
      downloadUrl,
      csvFilename,
      caseInfo,
      user,
      emailContext: 'Offers'
    });
    
    if (response.data.success) {
      return {
        success: true,
        subject: response.data.subject,
        body: response.data.body, // Backend now includes download button at the top
        assessorName: response.data.assessor_name,
        assessorEmail: response.data.assessor_email,
        offersCount: response.data.offers_count,
        toEmails: Array.isArray(response.data.assessor_email) ? response.data.assessor_email : (response.data.assessor_email ? [response.data.assessor_email] : []),
        ccEmails: [],
        downloadUrl: downloadUrl,
        csvFilename: csvFilename
      };
    } else {
      throw new Error(response.data.error || 'Failed to generate email preview');
    }
    
  } catch (error) {
    logger.error('Error generating email preview:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to generate email preview'
    };
  }
};

// EmailEditor component using iframe for WYSIWYG editing
const EmailEditor = ({
  initialHtml,
  onSave,
  editableSelectors = ['[data-editable="true"]'],
  allowFullBodyEdit = false,
}) => {
  const iframeRef = useRef(null);
  const hasUserEdited = useRef(false);
  const isInitialized = useRef(false);

  // Load initial HTML only once when component mounts
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || isInitialized.current) return;

    // Remove sandbox restrictions to allow editing
    iframe.removeAttribute("sandbox");

    const doc = iframe.contentDocument;
    if (!doc) return;

    doc.open();
    doc.write(initialHtml);
    doc.close();
  }, [initialHtml]);

  // Set up editing functionality
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // Wait for iframe to load, then make content editable
    const onLoad = () => {
      const iframeDoc = iframe.contentDocument;
      if (!iframeDoc) return;

      const makeEditable = () => {
        const selectors = editableSelectors;
        if (allowFullBodyEdit) {
          iframeDoc.body.contentEditable = "true";
          iframeDoc.body.setAttribute("spellcheck", "true");
        } else {
          let foundEditableElements = 0;
          selectors.forEach(sel => {
            iframeDoc.querySelectorAll(sel).forEach((n) => {
              n.setAttribute("contenteditable", "true");
              n.setAttribute("spellcheck", "true");
              foundEditableElements++;
            });
          });
          
          // Fallback: if no elements found with selectors, make the body editable
          if (foundEditableElements === 0) {
            logger.log('No editable elements found with selectors, making body editable');
            iframeDoc.body.contentEditable = "true";
            iframeDoc.body.setAttribute("spellcheck", "true");
          }
        }
      };

      // Guard against deletion of non-editable containers
      const guardKeys = (e) => {
        if (allowFullBodyEdit) return;
        const isDanger = e.key === "Backspace" || e.key === "Delete";
        if (!isDanger) return;
        const sel = iframeDoc.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        let node = sel.anchorNode;
        while (node && node.nodeType === 3) node = node.parentElement;
        if (!node) return;
        const withinEditable = (el) => {
          if (!el) return false;
          if (el.isContentEditable) return true;
          return withinEditable(el.parentElement);
        };
        if (!withinEditable(node)) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      // Store the current content without triggering parent re-renders
      const updateContent = () => {
        const doc = iframe.contentDocument;
        if (!doc) return;

        // Mark that user has made edits
        hasUserEdited.current = true;

        // Extract the whole HTML doc
        let rawHtml = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;

        // Strip our enhancer script before saving
        rawHtml = rawHtml.replace(/<script>[^<]*makeEditable[\s\S]*?<\/script>/gi, "");

        // Basic sanitization
        const clean = rawHtml
          .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '') // Remove all scripts
          .replace(/on\w+="[^"]*"/gi, '') // Remove event handlers
          .replace(/javascript:/gi, ''); // Remove javascript: URLs

        // Store in a ref to avoid re-renders
        if (iframeRef.current) {
          iframeRef.current._lastContent = clean;
        }
      };

      // Initialize editing
      makeEditable();
      
      // Mark as initialized
      isInitialized.current = true;
      
      iframeDoc.addEventListener('keydown', guardKeys, true);
      iframeDoc.addEventListener('input', updateContent, true);
      iframeDoc.addEventListener('paste', updateContent, true);
      
      // Add click handler to ensure focus
      iframeDoc.addEventListener('click', (e) => {
        if (e.target.contentEditable === 'true' || e.target.closest('[contenteditable="true"]')) {
          e.target.focus();
        }
      });
      
    };

    iframe.addEventListener("load", onLoad);
    
    // If iframe is already loaded, run immediately
    if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
      onLoad();
    }

    return () => {
      iframe.removeEventListener("load", onLoad);
    };
  }, [editableSelectors, allowFullBodyEdit]);

  // legacy function that is no longer used
  const handleSave = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // Get the last stored content
    const content = iframe._lastContent;
    if (content) {
      onSave(content);
    }
  }, [onSave]);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <iframe
        ref={iframeRef}
        style={{ 
          width: "100%", 
          height: "100%", 
          border: "1px solid #ddd", 
          borderRadius: 4,
          flex: 1
        }}
        title="Email Editor"
      />
    </Box>
  );
};

const EmailPreviewDialog = ({ 
  open, 
  onClose, 
  onConfirm, 
  emailData, 
  toEmails, 
  setToEmails, 
  ccEmails, 
  setCcEmails,
  bccEmails,
  setBccEmails,
  subject,
  setSubject,
  emailBody,
  setEmailBody,
  isGenerating = false,
  maxRounds = 1,
  selectedRound,
  setSelectedRound
}) => {
  const [newToEmail, setNewToEmail] = useState('');
  const [newCcEmail, setNewCcEmail] = useState('');
  const [newBccEmail, setNewBccEmail] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [editedEmailHtml, setEditedEmailHtml] = useState('');
  const [toEmailError, setToEmailError] = useState(false);
  const [ccEmailError, setCcEmailError] = useState(false);
  const [bccEmailError, setBccEmailError] = useState(false);

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleAddToEmail = () => {
    const trimmedEmail = newToEmail.trim();
    if (trimmedEmail && !toEmails.includes(trimmedEmail)) {
      if (isValidEmail(trimmedEmail)) {
        setToEmails([...toEmails, trimmedEmail]);
        setNewToEmail('');
        setToEmailError(false);
      } else {
        setToEmailError(true);
      }
    }
  };

  const handleAddCcEmail = () => {
    const trimmedEmail = newCcEmail.trim();
    if (trimmedEmail && !ccEmails.includes(trimmedEmail)) {
      if (isValidEmail(trimmedEmail)) {
        setCcEmails([...ccEmails, trimmedEmail]);
        setNewCcEmail('');
        setCcEmailError(false);
      } else {
        setCcEmailError(true);
      }
    }
  };

  const handleAddBccEmail = () => {
    const trimmedEmail = newBccEmail.trim();
    if (trimmedEmail && !bccEmails.includes(trimmedEmail)) {
      if (isValidEmail(trimmedEmail)) {
        setBccEmails([...bccEmails, trimmedEmail]);
        setNewBccEmail('');
        setBccEmailError(false);
      } else {
        setBccEmailError(true);
      }
    }
  };

  // Blur handlers for auto-adding valid emails
  const handleToEmailBlur = () => {
    if (newToEmail.trim()) {
      handleAddToEmail();
    }
  };

  const handleCcEmailBlur = () => {
    if (newCcEmail.trim()) {
      handleAddCcEmail();
    }
  };

  const handleBccEmailBlur = () => {
    if (newBccEmail.trim()) {
      handleAddBccEmail();
    }
  };

  // Input change handlers to clear error states
  const handleToEmailChange = (e) => {
    setNewToEmail(e.target.value);
    if (toEmailError) {
      setToEmailError(false);
    }
  };

  const handleCcEmailChange = (e) => {
    setNewCcEmail(e.target.value);
    if (ccEmailError) {
      setCcEmailError(false);
    }
  };

  const handleBccEmailChange = (e) => {
    setNewBccEmail(e.target.value);
    if (bccEmailError) {
      setBccEmailError(false);
    }
  };

  const handleRemoveToEmail = (emailToRemove) => {
    setToEmails(toEmails.filter(email => email !== emailToRemove));
  };

  const handleRemoveCcEmail = (emailToRemove) => {
    setCcEmails(ccEmails.filter(email => email !== emailToRemove));
  };

  const handleRemoveBccEmail = (emailToRemove) => {
    setBccEmails(bccEmails.filter(email => email !== emailToRemove));
  };

  const handleKeyPress = (e, type) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (type === 'to') {
        handleAddToEmail();
      } else if (type === 'cc') {
        handleAddCcEmail();
      } else if (type === 'bcc') {
        handleAddBccEmail();
      }
    }
  };

  // Prepare email HTML with editable regions marked
  const prepareEditableEmailHtml = (emailBody) => {
    if (!emailBody) return '';
    
    // Wrap the email body in a proper HTML document
    const fullHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 0; 
            padding: 20px; 
            line-height: 1.6;
            color: #333;
          }
          h1, h2, h3 { 
            color: #202124; 
            margin-bottom: 12px; 
            font-size: 16px; 
          }
          ul { 
            padding-left: 24px; 
            margin: 6px 0; 
          }
          .offer-item { 
            background-color: #f8f9fa; 
            padding: 12px; 
            margin: 2px 0; 
            border-left: 3px solid #1a73e8; 
            border-radius: 4px; 
            font-size: 14px; 
          }
          .stats { 
            display: flex; 
            flex-wrap: wrap; 
            gap: 8px; 
            margin: 8px 0; 
          }
          .stat-item { 
            background-color: #e8f0fe; 
            padding: 12px; 
            border-radius: 4px; 
            min-width: 100px; 
            font-size: 13px; 
          }
          p { 
            font-size: 14px; 
            line-height: 1.5; 
            margin: 2px 0; 
          }
          li { 
            font-size: 14px; 
            margin: 1px 0; 
          }
          [data-editable="true"] {
            outline: 1px dashed #1a73e8;
            outline-offset: 2px;
            min-height: 20px;
            cursor: text;
            position: relative;
          }
          [data-editable="true"]:hover {
            outline: 2px dashed #1a73e8;
            background-color: rgba(26, 115, 232, 0.05);
          }
          [data-editable="true"]:focus {
            outline: 2px solid #1a73e8;
            outline-offset: 2px;
            background-color: rgba(26, 115, 232, 0.1);
          }
          [contenteditable="true"] {
            outline: 1px dashed #1a73e8;
            outline-offset: 2px;
            min-height: 20px;
            cursor: text;
          }
          [contenteditable="true"]:hover {
            outline: 2px dashed #1a73e8;
            background-color: rgba(26, 115, 232, 0.05);
          }
          [contenteditable="true"]:focus {
            outline: 2px solid #1a73e8;
            outline-offset: 2px;
            background-color: rgba(26, 115, 232, 0.1);
          }
        </style>
      </head>
      <body>
        ${emailBody}
      </body>
      </html>
    `;
    
    // Add data-editable="true" to text content areas
    return fullHtml
      .replace(/<p([^>]*)>/g, '<p$1 data-editable="true">')
      .replace(/<div([^>]*)>/g, '<div$1 data-editable="true">')
      .replace(/<span([^>]*)>/g, '<span$1 data-editable="true">');
  };

  // Handle save from iframe editor
  const handleEmailEditorSave = (editedHtml) => {
    // Extract just the body content from the full HTML
    const bodyMatch = editedHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    const bodyContent = bodyMatch ? bodyMatch[1] : editedHtml;
    
    // Remove data-editable attributes
    const cleanContent = bodyContent.replace(/\s*data-editable="true"/g, '');
    
    setEditedEmailHtml(cleanContent);
    setEmailBody(cleanContent);
  };

  return (
    <StableMuiDialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          height: '85vh',
          borderRadius: '8px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1, 
        fontSize: '18px', 
        fontWeight: 500,
        color: '#202124',
        borderBottom: '1px solid #dadce0'
      }}>
        Email Preview & Send
      </DialogTitle>
      <DialogContent sx={{ 
        p: 0, 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        minHeight: 0 // Allow content to shrink
      }}>
        <Box sx={{ 
          p: 2, 
          pt: 0, 
          pb: 0,
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: 0, // Allow content to shrink
          overflow: 'hidden' // Prevent overflow
        }}>
          {/* Email Recipients - Gmail Style */}
          <Box sx={{
            borderBottom: '1px solid #dadce0',
            backgroundColor: '#ffffff',
            mb: 0.5,
            '&:hover': {
              borderBottomColor: '#1a73e8',
            },
            '&:focus-within': {
              borderBottomColor: '#1a73e8',
            },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  minWidth: '60px', 
                  fontSize: '13px', 
                  color: '#5f6368',
                  fontWeight: 500
                }}
              >
                To:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', flex: 1, minHeight: '32px' }}>
                {toEmails.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => handleRemoveToEmail(email)}
                    color="primary"
                    size="small"
                    sx={{
                      height: '24px',
                      fontSize: '13px',
                      margin: '2px',
                      '& .MuiChip-label': {
                        padding: '0 8px',
                      },
                      '& .MuiChip-deleteIcon': {
                        fontSize: '16px',
                      },
                    }}
                  />
                ))}
                <TextField
                  placeholder="Add email address"
                  value={newToEmail}
                  onChange={handleToEmailChange}
                  onBlur={handleToEmailBlur}
                  onKeyPress={(e) => handleKeyPress(e, 'to')}
                  variant="outlined"
                  size="small"
                  autoComplete="section-to email"
                  error={toEmailError}
                  inputProps={{
                    name: 'toEmail',
                    id: 'to-email',
                    inputMode: 'email',
                    autoCapitalize: 'none',
                    spellCheck: 'false'
                  }}
                  sx={{ 
                    minWidth: '200px',
                    '& .MuiOutlinedInput-root': {
                      height: '32px',
                      fontSize: '14px',
                      backgroundColor: 'transparent',
                      color: toEmailError ? '#d32f2f' : 'inherit',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                      '&.Mui-error fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '13px',
                      color: toEmailError ? '#d32f2f' : '#5f6368',
                      '&.Mui-focused': {
                        color: toEmailError ? '#d32f2f' : '#1a73e8',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: toEmailError ? '#d32f2f' : 'inherit',
                    },
                  }}
                />
                <IconButton 
                  onClick={handleAddToEmail} 
                  size="small"
                  sx={{ 
                    ml: 0.5,
                    p: 0.5,
                    color: '#1a73e8',
                    '&:hover': { backgroundColor: '#e8f0fe' }
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                <Button
                  size="small"
                  onClick={() => setShowCc(!showCc)}
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '12px',
                    color: showCc ? '#1a73e8' : '#5f6368',
                    minWidth: 'auto',
                    px: 1
                  }}
                >
                  Cc
                </Button>
                <Button
                  size="small"
                  onClick={() => setShowBcc(!showBcc)}
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '12px',
                    color: showBcc ? '#1a73e8' : '#5f6368',
                    minWidth: 'auto',
                    px: 1
                  }}
                >
                  Bcc
                </Button>
              </Box>
            </Box>

             {/* CC Field - Only show when toggled */}
             {showCc && (
               <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                 <Typography 
                   variant="body2" 
                   sx={{ 
                     minWidth: '60px', 
                     fontSize: '13px', 
                     color: '#5f6368',
                     fontWeight: 500
                   }}
                 >
                   Cc:
                 </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', flex: 1, minHeight: '32px' }}>
                  {ccEmails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveCcEmail(email)}
                      color="secondary"
                      size="small"
                      sx={{
                        height: '24px',
                        fontSize: '13px',
                        margin: '2px',
                        '& .MuiChip-label': {
                          padding: '0 8px',
                        },
                        '& .MuiChip-deleteIcon': {
                          fontSize: '16px',
                        },
                      }}
                    />
                  ))}
                   <TextField
                     placeholder="Add CC email address"
                     value={newCcEmail}
                     onChange={handleCcEmailChange}
                     onBlur={handleCcEmailBlur}
                     onKeyPress={(e) => handleKeyPress(e, 'cc')}
                     onFocus={(e) => e.target.removeAttribute('readonly')}
                     onKeyDown={(e) => e.currentTarget.removeAttribute('readonly')}
                     variant="outlined"
                     size="small"
                     autoComplete="off"
                     error={ccEmailError}
                     inputProps={{
                       name: 'ccAddress',
                       id: 'cc-email',
                       inputMode: 'email',
                       autoCapitalize: 'none',
                       spellCheck: 'false',
                       readOnly: true,
                       'data-bwignore': 'true',
                       'data-lpignore': 'true',
                       'data-1p-ignore': 'true',
                       'data-op-ignore': 'true',
                       'data-form-type': 'other'
                     }}
                     sx={{ 
                       minWidth: '200px',
                       '& .MuiOutlinedInput-root': {
                         height: '32px',
                         fontSize: '14px',
                         backgroundColor: 'transparent',
                         color: ccEmailError ? '#d32f2f' : 'inherit',
                         '& fieldset': {
                           border: 'none',
                         },
                         '&:hover fieldset': {
                           border: 'none',
                         },
                         '&.Mui-focused fieldset': {
                           border: 'none',
                         },
                         '&.Mui-error fieldset': {
                           border: 'none',
                         },
                       },
                       '& .MuiInputLabel-root': {
                         fontSize: '13px',
                         color: ccEmailError ? '#d32f2f' : '#5f6368',
                         '&.Mui-focused': {
                           color: ccEmailError ? '#d32f2f' : '#1a73e8',
                         },
                       },
                       '& .MuiInputBase-input': {
                         color: ccEmailError ? '#d32f2f' : 'inherit',
                       },
                     }}
                   />
                  <IconButton 
                    onClick={handleAddCcEmail} 
                    size="small"
                    sx={{ 
                      ml: 0.5,
                      p: 0.5,
                      color: '#1a73e8',
                      '&:hover': { backgroundColor: '#e8f0fe' }
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}

             {/* BCC Field - Only show when toggled */}
             {showBcc && (
               <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                 <Typography 
                   variant="body2" 
                   sx={{ 
                     minWidth: '60px', 
                     fontSize: '13px', 
                     color: '#5f6368',
                     fontWeight: 500
                   }}
                 >
                   Bcc:
                 </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', flex: 1, minHeight: '32px' }}>
                  {bccEmails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveBccEmail(email)}
                      color="default"
                      size="small"
                      sx={{
                        height: '24px',
                        fontSize: '13px',
                        margin: '2px',
                        '& .MuiChip-label': {
                          padding: '0 8px',
                        },
                        '& .MuiChip-deleteIcon': {
                          fontSize: '16px',
                        },
                      }}
                    />
                  ))}
                   <TextField
                     placeholder="Add BCC email address"
                     value={newBccEmail}
                     onChange={handleBccEmailChange}
                     onBlur={handleBccEmailBlur}
                     onKeyPress={(e) => handleKeyPress(e, 'bcc')}
                     onFocus={(e) => e.target.removeAttribute('readonly')}
                     onKeyDown={(e) => e.currentTarget.removeAttribute('readonly')}
                     variant="outlined"
                     size="small"
                     autoComplete="off"
                     error={bccEmailError}
                     inputProps={{
                       name: 'bccAddress',
                       id: 'bcc-email',
                       inputMode: 'email',
                       autoCapitalize: 'none',
                       spellCheck: 'false',
                       readOnly: true,
                       'data-bwignore': 'true',
                       'data-lpignore': 'true',
                       'data-1p-ignore': 'true',
                       'data-op-ignore': 'true',
                       'data-form-type': 'other'
                     }}
                     sx={{ 
                       minWidth: '200px',
                       '& .MuiOutlinedInput-root': {
                         height: '32px',
                         fontSize: '14px',
                         backgroundColor: 'transparent',
                         color: bccEmailError ? '#d32f2f' : 'inherit',
                         '& fieldset': {
                           border: 'none',
                         },
                         '&:hover fieldset': {
                           border: 'none',
                         },
                         '&.Mui-focused fieldset': {
                           border: 'none',
                         },
                         '&.Mui-error fieldset': {
                           border: 'none',
                         },
                       },
                       '& .MuiInputLabel-root': {
                         fontSize: '13px',
                         color: bccEmailError ? '#d32f2f' : '#5f6368',
                         '&.Mui-focused': {
                           color: bccEmailError ? '#d32f2f' : '#1a73e8',
                         },
                       },
                       '& .MuiInputBase-input': {
                         color: bccEmailError ? '#d32f2f' : 'inherit',
                       },
                     }}
                   />
                  <IconButton 
                    onClick={handleAddBccEmail} 
                    size="small"
                    sx={{ 
                      ml: 0.5,
                      p: 0.5,
                      color: '#1a73e8',
                      '&:hover': { backgroundColor: '#e8f0fe' }
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>

          {/* Subject Field - Gmail Style */}
          <Box sx={{
            borderBottom: '1px solid #dadce0',
            backgroundColor: '#ffffff',
            mb: 0,
            '&:hover': {
              borderBottomColor: '#1a73e8',
            },
            '&:focus-within': {
              borderBottomColor: '#1a73e8',
            },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', padding: '0px'}}>
              <Typography 
                variant="body2" 
                sx={{ 
                  padding: '0px',
                  mb: 0,
                  minWidth: '60px', 
                  fontSize: '13px', 
                  color: '#5f6368',
                  fontWeight: 500
                }}
              >
                Subject:
              </Typography>
              <TextField
                fullWidth
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                variant="outlined"
                size="small"
                placeholder="Enter email subject"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '32px',
                    fontSize: '14px',
                    backgroundColor: 'transparent',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px',
                    color: '#5f6368',
                    '&.Mui-focused': {
                      color: '#1a73e8',
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* File Generation */}

          {/* Email Body - Always Editable */}
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              minHeight: 0, // Allow flex shrinking
              height: '100%', // Fill available space
              overflow: 'hidden' // Prevent overflow
            }}>
              <Box sx={{ 
                backgroundColor: '#ffffff',
                marginTop: 0,
                flex: 1, // Fill available space
                width: '100%', // Use full width
                minHeight: 0, // Allow flex shrinking
                overflow: 'auto',
                height: '100%',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#dadce0',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#bdc1c6',
                },
                '&::-webkit-scrollbar-corner': {
                  background: 'transparent',
                }
              }}>
                {isGenerating ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 4, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary">
                      Generating email content...
                    </Typography>
                  </Box>
                ) : emailData ? (
                  <EmailEditor
                    initialHtml={prepareEditableEmailHtml(emailBody || emailData.body)}
                    onSave={handleEmailEditorSave}
                    editableSelectors={['[data-editable="true"]']}
                    allowFullBodyEdit={false}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No email content available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        p: 2, 
        borderTop: '1px solid #dadce0',
        backgroundColor: '#f8f9fa',
        borderRadius: '0 0 8px 8px'
      }}>
        <Button 
          onClick={() => onClose(false)} 
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            color: '#5f6368'
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={() => onClose(true)} 
          variant="contained"
          disabled={toEmails.length === 0 || isGenerating}
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            backgroundColor: '#1a73e8',
            '&:hover': {
              backgroundColor: '#1557b0',
            },
            '&.Mui-disabled': {
              backgroundColor: '#dadce0',
              color: '#5f6368'
            }
          }}
        >
          {isGenerating ? 'Generating...' : 'Send Email'}
        </Button>
      </DialogActions>
    </StableMuiDialog>
  );
};

/**
 * Hook for managing email preview dialog state
 */
export const useEmailPreviewDialog = (downloadButtonProps = {}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCallback, setDialogCallback] = useState(null);
  const [emailData, setEmailData] = useState(null);
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [editedEmailHtml, setEditedEmailHtml] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedRound, setSelectedRound] = useState(1);
  const [maxRounds, setMaxRounds] = useState(1);
  
  // Get S3 upload result from persisted state
  
//   In this dialog we have a stale closure somehow with the s3UploadResult
  const showDialog = async (emailPreviewData, callback) => {
    setDialogCallback(() => callback);
    setDialogOpen(true);
    setIsGenerating(true);
    
    try {
      // Generate email preview - use S3 upload result from parameters or store
      logger.log('emailPreviewData', emailPreviewData);
      const s3Result = emailPreviewData.s3UploadResult;
      
      // Extract user name and email from user object
      const userForBackend = emailPreviewData.user ? {
        userName: emailPreviewData.user.userName || 'Aventine',
        email: emailPreviewData.user.email || '',
        userId: emailPreviewData.user.userId || null
      } : { userName: 'Aventine', email: '', userId: null };
      
      const result = await generateEmailPreview({
        ...emailPreviewData,
        user: userForBackend,
        downloadUrl: s3Result?.downloadUrl || null,
        csvFilename: s3Result?.csvFilename || null
      });
      
      if (result.success) {
        setEmailData({
          ...result,
          finalizedOffers: emailPreviewData.finalizedOffers,
          allRows: emailPreviewData.allRows,
          muniCode: emailPreviewData.muniCode,
          taxYear: emailPreviewData.taxYear,
          villageRun: emailPreviewData.villageRun
        });
        setToEmails(result.toEmails || []);
        // Set default CC to user's email if available
        const defaultCcEmails = result.ccEmails || [];
        if (emailPreviewData.user?.email && !defaultCcEmails.includes(emailPreviewData.user.email)) {
          defaultCcEmails.push(emailPreviewData.user.email);
        }
        setCcEmails(defaultCcEmails);
        setBccEmails(result.bccEmails || []);
        setSubject(result.subject || '');
        setEmailBody(result.body || '');
        setEditedEmailHtml(result.body || '');
        
        // Set rounds data
        const rounds = emailPreviewData.maxRounds || 1;
        const initialRound = emailPreviewData.selectedRound || rounds;
        setMaxRounds(rounds);
        setSelectedRound(initialRound); // Use selected round from resend dialog if available, otherwise highest round
      } else {
        logger.error('Failed to generate email preview:', result.error);
        setEmailData(null);
        setToEmails([]);
        setCcEmails([]);
        setBccEmails([]);
        setSubject('');
        setEmailBody('');
        setEditedEmailHtml('');
      }
    } catch (error) {
      logger.error('Error in showDialog:', error);
      setEmailData(null);
      setToEmails([]);
      setCcEmails([]);
      setBccEmails([]);
      setSubject('');
      setEmailBody('');
      setEditedEmailHtml('');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDialogClose = (confirmed) => {
    // Extract the latest content from the iframe before closing
    let finalEmailBody = editedEmailHtml || emailBody;
    
    if (confirmed && emailData) {
      // Try to get the latest content from the iframe
      const iframe = document.querySelector('iframe[title="Email Editor"]');
      if (iframe && iframe.contentDocument) {
        const doc = iframe.contentDocument;
        let rawHtml = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
        rawHtml = rawHtml.replace(/<script>[^<]*makeEditable[\s\S]*?<\/script>/gi, "");
        const clean = rawHtml
          .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
          .replace(/on\w+="[^"]*"/gi, '')
          .replace(/javascript:/gi, '');
        
        // Extract just the body content
        const bodyMatch = clean.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        const bodyContent = bodyMatch ? bodyMatch[1] : clean;
        const finalContent = bodyContent.replace(/\s*data-editable="true"/g, '');
        
        
        if (finalContent) {
          finalEmailBody = finalContent;
        } else {
        }
      } else {
        logger.log('Could not access iframe or contentDocument');
      }
    }
    
    setDialogOpen(false);
    if (dialogCallback) {
      dialogCallback(confirmed, {
        toEmails,
        ccEmails,
        bccEmails,
        subject,
        emailBody: finalEmailBody, // Use the final extracted content
        emailData,
        selectedRound
      });
      setDialogCallback(null);
    }
  };

  const setGenerating = (generating) => {
    setIsGenerating(generating);
  };



  const dialogComponent = (
    <EmailPreviewDialog
      open={dialogOpen}
      onClose={handleDialogClose}
      onConfirm={handleDialogClose}
      emailData={emailData}
      toEmails={toEmails}
      setToEmails={setToEmails}
      ccEmails={ccEmails}
      setCcEmails={setCcEmails}
      bccEmails={bccEmails}
      setBccEmails={setBccEmails}
      subject={subject}
      setSubject={setSubject}
      emailBody={emailBody}
      setEmailBody={setEmailBody}
      isGenerating={isGenerating}
      maxRounds={maxRounds}
      selectedRound={selectedRound}
      setSelectedRound={setSelectedRound}
    />
  );

  return {
    showDialog,
    dialogComponent,
    setGenerating
  };
};
